import { Children, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CirclePlus, CircleMinus } from 'lucide-react';
import cn from 'classnames';

import { IconButton } from '@guestyci/arc';
import Dropdown from '@guestyci/foundation/Dropdown';
import t from '@guestyci/localize/t.macro';
import { Row } from '@guestyci/foundation/Layout';
import TextField from '@guestyci/foundation/TextField';
import createStyles from '@guestyci/foundation/createStyles';

import useGetRoomsLabel from 'hooks/useGetRoomsLabel';
import IconInput from 'components/IconInput';

const useDropdownOptionStyles = createStyles((theme) => ({
  root: {
    padding: [15, 12],
    userSelect: 'none',
  },
  valueLabel: {
    color: theme.palette.label,
    fontSize: 14,
    fontWeight: 'bold',
  },
  button: {
    '&:hover svg': {
      fill: '#2053f8',
      stroke: '#2053f8',
    },
    '&:hover path': {
      fill: '#fff',
      stroke: '#fff',
    },
  },
  controlsWrapper: {
    width: 84,
    justifyContent: 'space-between',
  },
}));

export const DropdownOption = ({
  value = 0,
  handleChange = () => {},
  label,
  min = 1,
  max = Infinity,
  disable = false,
  onChange = () => {},
}) => {
  const { root, button, valueLabel, controlsWrapper } = useDropdownOptionStyles();
  const inputRef = useRef();
  const increment = () => {
    const newValue = Number(inputRef.current.value) + 1;
    if (newValue <= max) {
      handleChange({ target: { value: newValue } });
    }
    onChange(newValue);
  };

  const decrement = () => {
    const newValue = Number(inputRef.current.value) - 1;
    if (newValue >= min) {
      handleChange({ target: { value: newValue } });
    }
    onChange(newValue);
  };

  return (
    <Row className={root} fullWidth justify="between" align="center">
      <TextField varian="h5">{label}</TextField>
      <Row className={controlsWrapper} align="center">
        <input
          ref={inputRef}
          type="number"
          value={value}
          onChange={handleChange}
          min={min}
          max={max}
          className="d-none"
          aria-label={label}
        />
        <IconButton
          variant="secondary"
          onClick={decrement}
          disabled={disable || value <= min}
          aria-label={t(`Decrease {label}`, { label })}
          className={cn(button, {
            'gst-text-primary': !(disable || value <= min),
            'gst-text-muted-foreground': disable || value <= min,
          })}
        >
          <CircleMinus />
        </IconButton>
        <span className={valueLabel}>{value ?? 0}</span>
        <IconButton
          variant="secondary"
          onClick={increment}
          disabled={disable || value >= max}
          aria-label={t(`Increase {label}`, { label })}
          className={cn(button, {
            'gst-text-primary': !(disable || value >= max),
            'gst-text-muted-foreground': disable || value >= max,
          })}
        >
          <CirclePlus />
        </IconButton>
      </Row>
    </Row>
  );
};

DropdownOption.propTypes = {
  value: PropTypes.number.isRequired,
  handleChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
};

const renderSelected = (values) => {
  const filteredValues = values?.filter(({ value = 0 }) => value !== undefined && value !== null);
  return filteredValues?.map(({ label, value = 1 }, index) => {
    return `${value} ${label} ${index === filteredValues.length - 1 ? '' : '+ '}`;
  });
};

const GuestsAndRoomsDropdown = ({ children = [], className, withIcon = false, placeholder = null }) => {
  const { isRoomLabel } = useGetRoomsLabel();
  const [item, setItem] = useState([]);
  useEffect(() => {
    setItem([Children.toArray(children)?.map((child) => child.props)]);
  }, [children]);

  const placeholderText = placeholder ?? (isRoomLabel ? t('Guests and Rooms') : t('Guests and Units'));
  return (
    <Dropdown
      placeholder={placeholderText}
      inputProps={{
        noClear: true,
        readOnly: true,
        'aria-label': placeholderText,
      }}
      renderSelected={(values) => renderSelected(values[0])}
      value={item}
      noClear
      className={className}
      input={withIcon && <IconInput />}
    >
      {Children.toArray(children)}
    </Dropdown>
  );
};

GuestsAndRoomsDropdown.propTypes = {
  children: DropdownOption,
  className: PropTypes.string,
};

export default GuestsAndRoomsDropdown;
